import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Plus, Loader2, ChevronUp } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Textarea } from "../../components/ui/textarea";

import RiskMatrixEditor from "../../components/Risk Matrix/RiskMatrixEditor";
import RiskMatrixPreview from "../../components/Risk Matrix/RiskMatrixPreview";

import { getMatrix } from "../../helpers/riskHelper";
import { useToast } from "../../components/hooks/use-toast";
import useProject from "../../components/hooks/useProject";

import firestoreService from "../../services/firestoreService";
import riskMatrixService from "../../services/riskMatrixService";
import analyticsService from "../../services/analyticsService";

export default function ProjectEditor() {
  const { projectId } = useParams();
  const { project, setProject } = useProject(projectId);

  const [descWordCount, setDescWordCount] = useState(0);
  const [acronymCharCount, setAcronymCharCount] = useState(0);

  const [customMatrices, setCustomMatrices] = useState([]);
  const [selectedMatrix, setSelectedMatrix] = useState(null);
  const [showMatrixEditor, setShowMatrixEditor] = useState(false);

  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [isUpdatingProject, setIsUpdatingProject] = useState(false);
  const [isLoadingMatrix, setIsLoadingMatrix] = useState(false);

  const [isValid, setIsValid] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch custom matrices on component mount
  useEffect(() => {
    const loadCustomMatrices = async () => {
      try {
        const matrices = await riskMatrixService.getAllriskHelper();
        setCustomMatrices(matrices);
      } catch (error) {
        console.error("Error loading custom matrices:", error);

        toast({
          variant: "destructive",
          title: "Error loading custom matrices",
          description: error.message,
        });
      }
    };

    loadCustomMatrices();
  }, [toast]);

  // Update the useEffect for loading selected matrix
  useEffect(() => {
    const loadSelectedMatrix = async () => {
      if (!project || !project.riskMatrix) {
        setSelectedMatrix(null);
        return;
      }

      setIsLoadingMatrix(true);

      try {
        const matrix = await getMatrix(project.riskMatrix, firestoreService);

        if (matrix) {
          setSelectedMatrix(matrix);
        }
      } catch (error) {
        console.error("Error loading selected matrix:", error);

        toast({
          variant: "destructive",
          title: "Error loading risk matrix",
          description: error.message,
        });
      } finally {
        setIsLoadingMatrix(false);
      }
    };

    loadSelectedMatrix();
  }, [toast, project?.riskMatrix, customMatrices, project]); 

  // Validate form fields
  useEffect(() => {
    if (
      project &&
      project.name.length > 0 &&
      project.acronym.length > 0 &&
      project.description.length > 0 &&
      project.riskMatrix.length > 0
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [project]);

  const handleChange = (name, value) => {
    setProject((prev) => {
      // Acronym
      if (name === "acronym") {
        setAcronymCharCount(value.length);
      }

      // Description
      else if (name === "description") {
        const descWordCount = value.trim().split(/\s+/);
        setDescWordCount(descWordCount.length);
      }

      return { ...prev, [name]: value };
    });
  };

  const handleMatrixSave = async (matrixData) => {
    if (!matrixData) {
      setShowMatrixEditor(false);
      return;
    }

    try {
      // Actually create the risk matrix in the database
      const matrixId = await riskMatrixService.createRiskMatrix(matrixData);
      
      // Add the newly created matrix ID to the matrix object
      matrixData.id = matrixId;
      
      // Auto-select the newly created matrix
      handleChange("riskMatrix", matrixId);

      // Refresh matrices list
      const matrices = await riskMatrixService.getAllriskHelper();
      setCustomMatrices(matrices);

      // Hide the matrix editor after successful save
      setShowMatrixEditor(false);

      // Toast success
      toast({
        title: "Success",
        description: "Custom risk matrix created successfully",
      });
    } catch (error) {
      console.error("Error saving custom matrix:", error);

      toast({
        variant: "destructive",
        title: "Error saving custom matrix",
        description: error.message,
      });
    }
  };

  const createProject = async () => {
    setIsCreatingProject(true);

    try {
      const projectId = await firestoreService.create("projects", {
        name: project.name,
        description: project.description,
        context: project.context,
        riskMatrix: project.riskMatrix,
        roles: project.roles,
        acronym: project.acronym.toUpperCase(),
        hazardCounter: project.hazardCounter,
        mitigationCounter: project.mitigationCounter,
        hazardStatusCount: project.hazardStatusCount,
      });
      
      // Track project creation in analytics
      analyticsService.trackProjectCreation(projectId, project.projectTypes ? project.projectTypes.join(',') : 'unspecified');

      navigate(`/projects/${projectId}/properties`);
    } catch (error) {
      console.error("Error creating project:", error);
      
      // Track error in analytics
      analyticsService.trackError('project_creation_error', error.message, 'createProject');

      toast({
        variant: "destructive",
        title: "Error creating project",
        description: error.message,
      });
    } finally {
      setIsCreatingProject(false);
    }
  };

  const updateProject = async () => {
    setIsUpdatingProject(true);

    try {
      await firestoreService.update("projects", project.id, {
        name: project.name,
        description: project.description,
        context: project.context,
        riskMatrix: project.riskMatrix,
        roles: project.roles,
        acronym: project.acronym.toUpperCase(),
        hazardCounter: project.hazardCounter,
        mitigationCounter: project.mitigationCounter,
        hazardStatusCount: project.hazardStatusCount,
      });
      
      // Track project update in analytics
      analyticsService.trackProjectUpdate(project.id, 'basic_info');

      navigate("/projects");
    } catch (error) {
      console.error("Error updating project:", error);
      
      // Track error in analytics
      analyticsService.trackError('project_update_error', error.message, 'updateProject');

      toast({
        variant: "destructive",
        title: "Error updating project",
        description: error.message,
      });
    } finally {
      setIsUpdatingProject(false);
    }
  };

  const toggleMatrixEditor = () => {
    setShowMatrixEditor(!showMatrixEditor);
  };

  return (
    <>
      {project && (
        <Card className="h-full">
          <CardHeader>
            <CardTitle>{projectId ? project.name : "New project"}</CardTitle>
          </CardHeader>

          <CardContent>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="projectName">Name</Label>

                <Input
                  id="projectName"
                  value={project.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="projectAcronym">Acronym</Label>

                <Input
                  id="projectAcronym"
                  value={project.acronym}
                  maxLength={4}
                  onChange={(e) => handleChange("acronym", e.target.value)}
                  required
                  className="uppercase"
                />

                <p className="text-sm text-gray-500 mt-1">
                  {acronymCharCount}/4 characters
                </p>
              </div>

              <div>
                <Label htmlFor="projectDescription">Description</Label>

                <Textarea
                  id="projectDescription"
                  value={project.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  required
                />

                <p className="text-sm text-gray-500 mt-1">
                  {descWordCount}/250 words
                </p>
              </div>
            </div>

            <div className="mt-4">
              <Label>Risk Matrix</Label>

              <Select
                value={project.riskMatrix}
                onValueChange={(value) => handleChange("riskMatrix", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Risk Matrix" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="MIL-882E">MIL-882E</SelectItem>

                  <SelectItem value="Sample Matrix">
                    Sample Matrix
                  </SelectItem>

                  {customMatrices.map((matrix) => (
                    <SelectItem key={matrix.id} value={matrix.id}>
                      {matrix.name} (Custom)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                type="button"
                onClick={toggleMatrixEditor}
                variant="outline"
                className="my-4"
              >
                {showMatrixEditor ? (
                  <>
                    <ChevronUp className="mr-2 h-4 w-4" />
                    Hide Matrix Editor
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Custom Matrix
                  </>
                )}
              </Button>

              {showMatrixEditor && (
                <RiskMatrixEditor 
                  onSave={handleMatrixSave}
                  existingMatrices={customMatrices} 
                />
              )}
            </div>

            {isLoadingMatrix ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : selectedMatrix ? (
              <ScrollArea className="rounded-md border p-4">
                <RiskMatrixPreview matrix={selectedMatrix} />
              </ScrollArea>
            ) : null}
          </CardContent>

          <CardFooter className="mx-auto mt-4">
            <div className="w-full">
              <div className="flex justify-center">
                {/* Create button */}
                {!projectId && (
                  <Button
                    onClick={createProject}
                    disabled={!isValid || isCreatingProject}
                    className="min-w-[120px]"
                  >
                    {isCreatingProject ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        <span>Creating...</span>
                      </>
                    ) : (
                      <>
                        <span>Create</span>
                      </>
                    )}
                  </Button>
                )}

                {/* Update button */}
                {projectId && (
                  <Button
                    onClick={updateProject}
                    disabled={!isValid || isUpdatingProject}
                    className="min-w-[120px]"
                  >
                    {isUpdatingProject ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <span>Update</span>
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardFooter>
        </Card>
      )}
    </>
  );
}
